<template>
  <div class="min-h-screen flex bg-login">
    <div class="hidden lg:flex lg:col-6 align-items-center justify-content-center p-4">
      <div class="text-center">
        <img src="/siis-icon.png" alt="siis-icon" class="w-[30px] h-[30px]" />
        <p class="text-logo primaryFont">Sistema Integral Informático de Seguridad</p>
      </div>
    </div>

    <div class="flex col-12 lg:col-6 align-items-center justify-content-center p-3">
      <Card class="cardLogin">
        <template #header>
          <div class="text-center">
            <h1 class="primaryFont title">¡Bienvenido!</h1>
          </div>
        </template>

        <template #content>
          <!-- Formulario Login -->
          <div class="p-fluid formgrid grid gap-3 m-0" v-if="!loginSuccess">
            <div class="field mb-4 w-full" v-if="MailAvailable">
              <label for="inputuser" class="label">Nombre de usuario</label>
              <InputText
                v-no-emoji
                id="inputuser"
                placeholder="Ingresar usuario"
                test-id="campo-nombre-de-usuario"
                v-model="dataUsername"
                @keyup="cleaner"
                @keyup.enter="validations"
                :class="{ 'p-invalid': userError }"
                class="inputlogin w-full"
              />
            </div>
            <div v-else class="field col-12 md:col-12">
              <label class="label">Ingrese su DNI</label>
              <InputText
                v-no-emoji
                type="number"
                placeholder="Ingresar DNI"
                id="inputDNI"
                test-id="campo-dni"
                v-model="dni"
                @keyup="cleaner"
                @keyup.enter="validations"
                :class="{ 'p-invalid': dniError }"
                class="inputlogin w-full"
              />
            </div>
            <div class="field w-full">
              <label for="inputpass" class="label">Contraseña</label>
              <Password
                :feedback="false"
                toggleMask
                id="inputpass"
                placeholder="Ingresar contraseña"
                v-model="contrasena"
                test-id="input_password"
                @keyup="cleaner"
                @keyup.enter="validations"
                :class="{ 'p-invalid': passError }"
                class="w-full"
              />
            </div>
          </div>
          <!-- Formulario Código 2FA -->
          <div v-else class="p-fluid formgrid grid gap-3">
            <div class="field w-full">
              <InputOTP
                v-model="code"
                test-id="campo-de-codigo-autentificacion"
                :length="4"
                label="Código de autenticación"
                placeholder="•"
                :disabled="!reSendCode"
                :error="codeError"
                @complete="handleOtpComplete"
              />
            </div>
            <p class="text-auth-code px-2">
              Revisa tu correo electrónico. Ahí enviamos tu código de autenticación.
            </p>
          </div>
          <!-- Mensajes de Error -->
          <div v-if="error || (err && cleanError) || !online" class="mb-3">
            <p v-if="error" test-id="mensaje-error" class="error-message">
              {{ error }}
            </p>
            <p v-if="err && cleanError" class="error-message">
              {{ errorMsg }}
            </p>
            <p v-if="!online" class="error-message">
              El sistema se encuentra actualmente funcionando sin conexión a internet. Es posible
              que al restablecerse el servicio de internet le vuelva a pedir la contraseña.
            </p>
          </div>
        </template>

        <template #footer>
          <div v-if="!loginSuccess" class="flex flex-column gap-3">
            <Button
              v-if="MailAvailable"
              id="botonlogin"
              test-id="button_login"
              label="Iniciar sesión"
              @click="validations"
              class="w-full btn-login"
              :disabled="isLoginDisabled || isLoading"
            >
              <div v-if="isLoading" class="flex justify-content-center w-full">
                <ProgressSpinner style="width: 20px; height: 20px" />
              </div>
              <div v-else class="text-center w-full">Iniciar sesión</div>
            </Button>
            <div v-else>
              <Button
                id="botonsDNI"
                test-id="button_dni"
                label="Validar identidad"
                @click="validations('DNI')"
                class="w-full btn-login"
              />
              <div>{{ `Intentos restantes: ${MaxAttemptsDNI - countWrongDni}` }}</div>
            </div>
            <div class="text-center link-help">
              ¿Necesitás ayuda con tu acceso?<br />
              Contactá a la
              <a href="https://wa.me/+5492644446909" class="font-bold link-email"
                >Mesa de ayuda técnica.</a
              >
            </div>
          </div>
          <div v-else class="flex flex-column gap-3">
            <div class="flex align-items-center justify-content-between">
              <Button
                id="botontoken"
                test-id="button_token"
                label="Reenviar código"
                :disabled="reSendCode"
                class="p-button-info p-button-text p-0 text-resend-code"
                @click="sendCode"
              />
              <Timer
                :intensity="300"
                :duration="timeOut"
                v-if="reSendCode"
                class="w-1/2 text-resend-code"
              />
            </div>
            <div class="flex flex-column gap-2 w-full">
              <Button
                id="botonsendtoken"
                test-id="button_send_token"
                class="btn-login w-full"
                :disabled="isContinueDisabled"
                @click="validations('auth')"
              >
                <div v-if="isLoading" class="flex justify-content-center w-full">
                  <ProgressSpinner style="width: 20px; height: 20px" />
                </div>
                <div v-else class="text-center w-full">Continuar</div>
              </Button>
            </div>
          </div>
          <div v-if="isDevMode" class="mb-3 flex align-items-center gap-2">
            <input
              type="checkbox"
              id="mailAvailableToggle"
              :checked="MailAvailable"
              @change="toggleMailAvailable"
              class="checkbox-toggle"
            />
            <label for="mailAvailableToggle" class="label">
              {{ MailAvailable ? "Usar correo" : "Usar DNI" }}
            </label>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import { useStoreCompat } from "@/composables/useStoreCompat";
import { useRouterCompat } from "@/composables/useRouterCompat";
import Timer from "../components/Timer.vue";
import InputOTP from "../components/InputOTP.vue";

const props = defineProps({
  online: Boolean,
});

const store = useStoreCompat();
const { router, route } = useRouterCompat();
const instance = getCurrentInstance();

const isLoading = ref(false);
const timeOut = ref(2);
const username = ref("");
const password = ref("");
const dni = ref("");
const code = ref("");
const MaxAttemptsDNI = ref(2);
const error = ref("");
const userError = ref(false);
const passError = ref(false);
const codeError = ref(false);
const dniError = ref(false);
const errorMsg = ref("No existe usuario con esa contraseña");
const cleanError = ref(false);
const reSendCode = ref(true);
const isDevMode = process.env.VUE_APP_VUE_MODE === "development";

const toggleMailAvailable = () => {
  console.log("toggleMailAvailable", MailAvailable.value);
  store.commit("user/setMailAvailable", !MailAvailable.value);
};

const loginSuccess = computed(() => store.getters["user/loginSuccess"]);
const errorLogin = computed(() => store.getters["user/errorLogin"]);
const errorDni = computed(() => store.getters["user/errorDni"]);
const countWrongDni = computed(() => store.getters["user/countWrongDni"]);
const MailAvailable = computed(() => store.getters["user/MailAvailable"]);

const err = computed(() => store.state.user.err);

const dataUsername = computed({
  get: () => username.value,
  set: (value) => {
    username.value = value;
  },
});

const contrasena = computed({
  get: () => password.value,
  set: (value) => {
    password.value = value.trim();
  },
});

const isContinueDisabled = computed(() => {
  return !code.value || code.value.length !== 4 || isLoading.value;
});

const errorType = (type, msg) => {
  switch (type) {
    case "userError":
      userError.value = true;
      break;
    case "passError":
      passError.value = true;
      break;
    case "codeError":
      codeError.value = true;
      break;
    case "dniError":
      dniError.value = true;
      break;
    case "errorBD":
      // Para compatibilidad con el código existente
      break;
  }
  error.value = msg;
};

const cleaner = () => {
  error.value = "";
  cleanError.value = false;
  userError.value = false;
  passError.value = false;
  dniError.value = false;
  codeError.value = false;
};

const login = async () => {
  isLoading.value = true;
  const uri = window.location.search.substring(1);
  const params = new URLSearchParams(uri);

  store.dispatch("user/clearErrorLogin");
  store.dispatch("user/clearErrorDni");

  try {
    if (!MailAvailable.value) {
      await store.dispatch("user/login", {
        username: dataUsername.value,
        password: password.value,
        dni: dni.value,
      });
    } else {
      await store.dispatch("user/login", {
        username: username.value,
        password: password.value,
        code: code.value,
      });

      if (codeError.value) {
        errorType("errorBD", "El codigo no es correcto");
      }
    }

    if (errorDni.value) {
      errorType("error", "El dni o la contraseña no son correctos");
    } else if (errorLogin.value) {
      console.log("errorLogin", errorLogin.value);
      // Verificar si el mensaje de error contiene información de intentos restantes
      const intentosMatch = errorLogin.value.match(/Intentos restantes:\s*(-?\d+)/i);

      if (intentosMatch) {
        const intentosRestantes = parseInt(intentosMatch[1], 10);

        // Si los intentos restantes son 0 o negativos, resetear al login principal
        if (intentosRestantes <= 0) {
          errorType(
            "error",
            "Has agotado todos los intentos. Por favor, inicia sesión nuevamente."
          );
          // Limpiar el código OTP
          code.value = "";
          // Resetear el estado del usuario para volver al login principal
          setTimeout(() => {
            store.dispatch("user/clearUserState");
          }, 2000); // Dar tiempo para que el usuario vea el mensaje
          return;
        }
      }

      errorType("error", errorLogin.value);
    } else {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.ready.then((reg) => {
          reg.active?.postMessage({ action: "pre-cache-dynamic" });
        });
      }
      if (params.has("closeTab")) {
        window.close();
      } else {
        if (route.path !== "/") router.push("/");
      }
    }

    reSendCode.value = true;
    setTimeout(() => {
      reSendCode.value = false;
      if (loginSuccess.value === true) error.value = "Código vencido";
      code.value = "";
    }, timeOut.value * 60000);
  } catch (e) {
    console.error("login error", e);
  } finally {
    isLoading.value = false;
  }
};

const handleOtpComplete = async (value) => {
  code.value = value;
  cleaner();
};

const validations = async (type) => {
  error.value = "";

  if (countWrongDni.value >= MaxAttemptsDNI.value) {
    store.dispatch("user/toastWrongDni");
    store.dispatch("user/clearUserState");
    return;
  }

  if (type === "auth") {
    if (code.value.length !== 4) {
      errorType("codeError", "El código debe contener 4 dígitos");
    } else {
      await login();
    }
    return;
  }

  if (type === "DNI") {
    console.log("DNI", dni.value);
    if (dni.value.length < 3) {
      errorType("userError", "El dni debe tener al menos 3 caracteres");
    } else if (dni.value.length > 50) {
      errorType("userError", "El dni no debe tener más de 50 caracteres");
    } else {
      await login();
    }
    return;
  }

  if (username.value.length < 3) {
    errorType("userError", "El nombre de usuario debe tener al menos 3 caracteres");
  }
  if (username.value.length > 50) {
    errorType("userError", "El nombre de usuario no debe tener más de 50 caracteres");
  }
  if (!error.value) {
    await login();
  }
};

const sendCode = async () => {
  code.value = "";
  await login();
  timeOut.value = 0;
  setTimeout(() => {
    timeOut.value = 2;
  }, 1000);
};

const isLoginDisabled = computed(() => {
  if (!MailAvailable.value) {
    return !dni.value || dni.value.length < 3 || !password.value || password.value.length < 3;
  } else {
    return (
      !username.value || username.value.length < 3 || !password.value || password.value.length < 3
    );
  }
});

watch(username, (newValue) => {
  if (newValue === "test offline true") {
    instance?.proxy?.$offline?.setOfflineMode(true);
  } else if (newValue === "test offline false") {
    instance?.proxy?.$offline?.setOfflineMode(false);
  }
});

onMounted(() => {
  store.dispatch("user/checkLogin", { VueCli: instance?.proxy });
  if (store.state.user.logged) {
    router.push("/");
  }
});
</script>
<style scoped>
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100..900;1,100..900&display=swap");
.cardLogin {
  padding: 45px 95px 20px;
  max-width: 603px;
  min-height: 600px;
  background-color: #09447c;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.btn-login:disabled {
  background-color: #e0e0e0;
  color: #9e9e9e;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-login:disabled:hover {
  background-color: #e0e0e0;
  transform: none;
  box-shadow: none;
}

:deep(.p-card-body) {
  padding: 0 !important;
  margin-top: 20px;
}

:deep(.p-card-content) {
  padding: 0;
}

.spinner {
  display: none;
}
.bg-login {
  background: #f1f5f9e5;
  box-shadow: 0px 4px 4px 0px #00000040;
}

.min-h-screen {
  min-height: 100vh;
}

.title {
  font-size: 42px;
  font-weight: 700;
  color: #ffffff;
  font-family: "DM Sans", sans-serif;
  margin: 0;
}

.label {
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  font-family: "DM Sans", sans-serif;
}

.btn-login {
  background-color: #ffffff;
  color: #09447c;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  font-family: "Inter", sans-serif;
  min-height: 56px;
  width: 100%;
}

/* Inputs */
.inputlogin {
  background-color: transparent;
  border-radius: 12px;
  padding: 14px 20px;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  font-family: "DM Sans", sans-serif;
  color: #ffffff;
  border: 2px solid rgba(255, 255, 255, 0.4);
  transition: all 0.2s ease;
  width: 100%;
}

.inputlogin::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.inputlogin:enabled:hover {
  border-color: rgba(255, 255, 255, 0.6);
}

.inputlogin:enabled:focus {
  border-color: rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1);
}

/* Password Component */
:deep(.p-password) {
  width: 100%;
}

:deep(.p-password .p-inputtext) {
  background-color: transparent;
  border-radius: 12px;
  padding: 14px 20px;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  font-family: "DM Sans", sans-serif;
  color: #ffffff;
  border: 2px solid rgba(255, 255, 255, 0.4);
  transition: all 0.2s ease;
  width: 100%;
}

:deep(.p-password .p-inputtext::placeholder) {
  color: rgba(255, 255, 255, 0.5);
}
:deep(.p-password .p-inputtext:enabled:hover) {
  border-color: rgba(255, 255, 255, 0.6);
}

:deep(.p-password .p-inputtext:enabled:focus) {
  border-color: rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1);
}

:deep(.p-password-toggle-icon) {
  color: rgba(255, 255, 255, 0.7);
}

/* Links y ayuda */
.link-help {
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  font-family: "DM Sans", sans-serif;
  line-height: 20px;
}

.link-email {
  color: #ffffff;
  font-weight: 700;
  text-decoration: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);
  transition: border-color 0.2s ease;
}

.link-email a {
  text-decoration: none !important;
}

.text-logo {
  font-size: 24px;
  font-weight: 700;
  font-style: bold;
  color: #09447c;
  text-align: center;
}

/* Texto código autenticación */
.text-auth-code {
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  font-family: "DM Sans", sans-serif;
  line-height: 20px;
  margin: 0;
}

/* Texto reenviar código */
.text-resend-code {
  font-size: 14px;
  font-weight: 500;
  color: #ffffff !important;
  font-family: "DM Sans", sans-serif;
}

/* Mensajes de error */
.error-message {
  font-size: 13px;
  font-weight: 400;
  color: #ffb3b3;
  font-family: "DM Sans", sans-serif;
  margin: 4px 0;
  padding: 8px 12px;
  background-color: rgba(255, 107, 107, 0.15);
  border-radius: 8px;
  border-left: 3px solid #ff6b6b;
}
</style>
